import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Plus, RefreshCw, Save } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api, requestWithMessage, type DepartmentRef, type MailRecipientOption, type UserRef } from '../lib/api';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { DAY_NAMES, formatDateTime, formatDays, formatNumber, relativeTime } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Detail,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Select,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  statusTone,
  useToast,
} from '../components/ui';
import { AgentPolicyPanel } from '../components/AgentPolicyPanel';
import { PeoplePicker } from '../components/PeoplePicker';
import { WalletSettingsPanel } from '../components/WalletSettingsPanel';
import { SitesSettingsPanel } from '../components/SitesSettingsPanel';
import { CallsSettingsPanel } from '../components/CallsSettingsPanel';
import { CallsAiSettingsPanel } from '../components/CallsAiSettingsPanel';
import { ExecutiveDashboardPanel } from '../components/ExecutiveDashboardPanel';
import { P, useAuth } from '../lib/auth';

interface SettingSpec {
  key: string;
  label: TranslationKey;
  type: 'text' | 'number' | 'boolean' | 'select' | 'weekday' | 'days' | 'department' | 'link' | 'time';
  options?: string[];
  hint?: TranslationKey;
}

/**
 * §30 — the settings areas, in the order the spec lists them. Finance, payroll,
 * client numbering and currencies are changed in sys; the API refuses an admin
 * session that tries, and the other way round.
 *
 * A tab's id is its settings group — the part of each key before the dot — so
 * the values come straight out of `GET /settings`. Leave has no settings of its
 * own, only its types.
 */
const GROUPS: Array<{
  id: string;
  title: TranslationKey;
  note?: TranslationKey;
  settings: SettingSpec[];
  extra?: () => ReactNode;
  /** Shown only to whoever holds it — the tab's own permission, beside Settings' own. */
  permission?: string;
}> = [
  {
    id: 'company',
    title: 'settings.company',
    settings: [
      { key: 'company.name', label: 'settings.companyName', type: 'text' },
      { key: 'company.nameAr', label: 'settings.companyNameAr', type: 'text' },
      { key: 'company.domain', label: 'settings.domain', type: 'text', hint: 'settings.domainHint' },
      // Invoices and receipts print this one; the system's domain stays inside the company.
      { key: 'company.clientDomain', label: 'settings.clientDomain', type: 'text', hint: 'settings.clientDomainHint' },
      { key: 'company.timezone', label: 'settings.timezone', type: 'text', hint: 'settings.timezoneHint' },
      { key: 'company.weekStartDay', label: 'settings.weekStart', type: 'weekday' },
    ],
  },
  {
    id: 'sla',
    title: 'settings.sla',
    note: 'settings.slaNote',
    settings: [
      // §10.4 — the view SLA is also when the "nobody viewed it" alert goes out.
      { key: 'sla.viewSlaMinutes', label: 'settings.viewSlaMinutes', type: 'number', hint: 'settings.viewSlaHint' },
      { key: 'sla.assignmentSlaMinutes', label: 'settings.assignmentSlaMinutes', type: 'number' },
      // Penalties are days of the employee's own salary (0.25, 0.5 …), priced by payroll.
      { key: 'sla.viewPenaltyDays', label: 'settings.viewPenaltyDays', type: 'days', hint: 'settings.viewPenaltyHint' },
      {
        key: 'sla.assignmentPenaltyDays',
        label: 'settings.assignmentPenaltyDays',
        type: 'days',
        hint: 'settings.assignmentPenaltyHint',
      },
      // §11.4
      { key: 'sla.penaltiesAreCumulative', label: 'settings.penaltiesAreCumulative', type: 'boolean', hint: 'settings.cumulativeHint' },
      { key: 'sla.penaltyRequiresApproval', label: 'settings.penaltyRequiresApproval', type: 'boolean' },
      { key: 'sla.excludeOffShiftEmployees', label: 'settings.excludeOffShift', type: 'boolean' },
      // The operator's rule (2026-10-05): on a break or not started is told, never charged.
      {
        key: 'sla.excludeNotWorkingEmployees',
        label: 'settings.excludeNotWorking',
        type: 'boolean',
        hint: 'settings.excludeNotWorkingHint',
      },
      { key: 'sla.excludeInactiveEmployees', label: 'settings.excludeInactive', type: 'boolean' },
      // §10.3 rule 5 / §17.3
      { key: 'sla.excludeApprovedAbsence', label: 'settings.excludeAbsence', type: 'boolean', hint: 'settings.excludeAbsenceHint' },
      { key: 'sla.allowSelfAssignment', label: 'settings.allowSelfAssignment', type: 'boolean' },
      // §26.2
      { key: 'sla.warningMinutes', label: 'settings.warningMinutes', type: 'number' },
    ],
  },
  {
    id: 'tickets',
    title: 'settings.tickets',
    settings: [
      { key: 'tickets.numberPrefix', label: 'settings.numberPrefix', type: 'text' },
      { key: 'tickets.defaultPriority', label: 'settings.defaultPriority', type: 'select', options: ['LOW', 'MEDIUM', 'HIGH', 'URGENT'] },
      { key: 'tickets.lockMinutes', label: 'settings.lockMinutes', type: 'number' },
    ],
  },
  {
    // The client portal, clients.viganium.net (operator's request, 2026-10-03).
    // Each client's username and password are set on its own form, under Clients.
    id: 'portal',
    title: 'settings.portal',
    note: 'settings.portalNote',
    settings: [
      {
        key: 'portal.ticketDepartmentId',
        label: 'settings.portalDepartment',
        type: 'department',
        hint: 'settings.portalDepartmentHint',
      },
    ],
  },
  {
    // Our apps, for employees to download (operator, 2026-10-11): Viganium Team's APK, on R2 say.
    id: 'mobile',
    title: 'settings.mobile',
    note: 'settings.mobileNote',
    settings: [
      { key: 'mobile.androidDownloadUrl', label: 'settings.androidDownload', type: 'link', hint: 'settings.androidDownloadHint' },
      // A phone with an older build must update before the app opens again (2026-10-11).
      {
        key: 'mobile.androidLatestVersion',
        label: 'settings.androidLatestVersion',
        type: 'text',
        hint: 'settings.androidLatestVersionHint',
      },
    ],
  },
  {
    id: 'projects',
    title: 'settings.projects',
    settings: [
      // §7.6
      {
        key: 'projects.allowSupportDepartmentCrossProjectAccess',
        label: 'settings.crossProject',
        type: 'boolean',
        hint: 'settings.crossProjectHint',
      },
    ],
  },
  {
    id: 'shifts',
    title: 'settings.shifts',
    settings: [
      { key: 'shifts.defaultDailyHours', label: 'settings.defaultDailyHours', type: 'number', hint: 'settings.defaultDailyHoursHint' },
    ],
  },
  {
    id: 'attendance',
    title: 'settings.attendance',
    settings: [
      { key: 'attendance.promptOnLogin', label: 'settings.promptOnLogin', type: 'boolean', hint: 'settings.promptOnLoginHint' },
      { key: 'attendance.maxShiftHours', label: 'settings.maxShiftHours', type: 'number', hint: 'settings.maxShiftHoursHint' },
      // The daily-hours rule (operator, 2026-10-15): short of the Daily hours costs these days of
      // salary, and working them drops the day's break and inactivity penalties.
      { key: 'attendance.shortDayPenaltyDays', label: 'settings.shortDayPenalty', type: 'days', hint: 'settings.shortDayPenaltyHint' },
      {
        key: 'attendance.fullDayWaivesPenalties',
        label: 'settings.fullDayWaives',
        type: 'boolean',
        hint: 'settings.fullDayWaivesHint',
      },
      // The shift & working-hours audit (2026-10-21): minutes short that still count, and when the day turns.
      {
        key: 'attendance.dailyHoursToleranceMinutes',
        label: 'settings.dailyHoursTolerance',
        type: 'number',
        hint: 'settings.dailyHoursToleranceHint',
      },
      { key: 'attendance.dayStartsAt', label: 'settings.dayStartsAt', type: 'time', hint: 'settings.dayStartsAtHint' },
    ],
    // Who may pause work, beside the daily-hours rule it answers to; the Desktop Agent and the
    // idle policy; then the break types.
    extra: () => (
      <>
        <PausePanel />
        <AgentPolicyPanel />
        <BreakTypesPanel />
      </>
    ),
  },
  { id: 'leave', title: 'settings.leave', settings: [], extra: () => <LeaveTypesPanel /> },
  {
    // The meeting calendar (operator, 2026-10-21: "add remember before 15 min example make it in settings").
    id: 'meetings',
    title: 'settings.meetings',
    note: 'settings.meetingsNote',
    settings: [
      { key: 'meetings.reminderMinutes', label: 'settings.meetingReminder', type: 'number', hint: 'settings.meetingReminderHint' },
      {
        key: 'meetings.emailInvitations',
        label: 'settings.meetingEmailInvitations',
        type: 'boolean',
        hint: 'settings.meetingEmailInvitationsHint',
      },
      { key: 'meetings.emailReminders', label: 'settings.meetingEmailReminders', type: 'boolean' },
    ],
  },
  // Calls (2026-10-29): how long recordings are kept — forever until changed — and the ring, size and
  // length of a call. Its own panel: a shorter retention says how many recordings it deletes first.
  // Calls › AI summary (2026-10-29): its own panel after them — switched on only on a ready server, after a confirmation.
  {
    id: 'calls',
    title: 'settings.calls',
    settings: [],
    extra: () => (
      <>
        <CallsSettingsPanel />
        <CallsAiSettingsPanel />
      </>
    ),
  },
  // The points wallet (CR-WALLET-001, 2026-10-20): what each reward costs, and the cash rate.
  { id: 'wallet', title: 'settings.wallet', settings: [], extra: () => <WalletSettingsPanel />, permission: P.WALLET_SETTINGS_MANAGE },
  // Sites (2026-11-03): who may publish sites beside the administrators — departments and employees.
  { id: 'sites', title: 'settings.sites', settings: [], extra: () => <SitesSettingsPanel />, permission: P.SITES_MANAGE },
  // The Executive Dashboard (CR-008, 2026-11-04): who may open dashboard.viganium.net, and its settings.
  {
    id: 'executive',
    title: 'settings.executive',
    settings: [],
    extra: () => <ExecutiveDashboardPanel />,
    permission: P.DASHBOARD_ACCESS_MANAGE,
  },
  {
    id: 'notifications',
    title: 'settings.email',
    settings: [
      { key: 'notifications.emailEnabled', label: 'settings.emailEnabled', type: 'boolean', hint: 'settings.emailEnabledHint' },
    ],
    extra: () => <EmailPanel />,
  },
  {
    id: 'security',
    title: 'settings.security',
    settings: [
      { key: 'security.passwordMinLength', label: 'settings.passwordMinLength', type: 'number' },
      { key: 'security.maxUploadMb', label: 'settings.maxUploadMb', type: 'number', hint: 'settings.maxUploadMbHint' },
      {
        key: 'security.maxVideoUploadMb',
        label: 'settings.maxVideoUploadMb',
        type: 'number',
        hint: 'settings.maxVideoUploadMbHint',
      },
      {
        key: 'security.maxDriveFileMb',
        label: 'settings.maxDriveFileMb',
        type: 'number',
        hint: 'settings.maxDriveFileMbHint',
      },
    ],
  },
];

export function SettingsPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const groups = GROUPS.filter((g) => !g.permission || can(g.permission));
  // The tab is in the address, so a type's form page can send you back to it.
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab') ?? 'company';
  const tab = requested === 'jobs' || groups.some((g) => g.id === requested) ? requested : 'company';
  const group = groups.find((g) => g.id === tab);

  return (
    <>
      <PageHeader title={t('settings.title')} />
      <Tabs
        active={tab}
        onChange={(id) => setParams(id === 'company' ? {} : { tab: id }, { replace: true })}
        tabs={[
          ...groups.map((g) => ({ id: g.id, label: t(g.title) })),
          { id: 'jobs', label: t('settings.jobs') },
        ]}
      />
      <div className="mt-5 space-y-5">
        {tab === 'jobs' ? (
          <JobsPanel />
        ) : (
          <>
            {group && group.settings.length > 0 && <SettingsGroup groupId={tab} />}
            {group?.extra?.()}
          </>
        )}
      </div>
    </>
  );
}

function SettingsGroup({ groupId }: { groupId: string }) {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const group = GROUPS.find((g) => g.id === groupId)!;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Record<string, Record<string, unknown>>>('/settings'),
  });

  // Only the portal's tab picks a department.
  const needsDepartments = group.settings.some((spec) => spec.type === 'department');
  const { data: departments } = useQuery({
    queryKey: ['departments', 'options'],
    queryFn: () => api.get<{ items: Array<{ id: string; name: string; nameAr?: string | null; isActive?: boolean }> }>('/departments', { pageSize: 100 }),
    enabled: needsDepartments,
  });

  const [draft, setDraft] = useState<Record<string, unknown>>({});
  // What the API refused, field by field (a link that is not one): shown under its field, in the reader's language.
  const [problems, setProblems] = useState<Array<{ key: string; reason: string; limit?: number }>>([]);

  useEffect(() => {
    // Reset the draft whenever the tab or the server values change.
    setDraft({});
    setProblems([]);
  }, [groupId, data]);

  const save = useMutation({
    mutationFn: () => api.put('/settings', { values: draft }),
    onSuccess: () => {
      toast.success(t('settings.saved'));
      setDraft({});
      setProblems([]);
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      // The shift prompt and the email panel read these.
      void queryClient.invalidateQueries({ queryKey: ['attendance', 'me'] });
      void queryClient.invalidateQueries({ queryKey: ['email', 'status'] });
    },
    onError: (e: Error) => {
      const found =
        e instanceof ApiError ? ((e.details as { problems?: Array<{ key: string; reason: string }> } | undefined)?.problems ?? []) : [];
      setProblems(found);
      toast.error(found.length ? t('settings.fixMarked') : e.message);
    },
  });
  const problemFor = (key: string): string | null => {
    const p = problems.find((x) => x.key === key);
    return p ? t(`settings.problem.${p.reason}` as TranslationKey, { limit: p.limit ?? '' }) : null;
  };

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const current = (key: string): unknown => (key in draft ? draft[key] : data?.[groupId]?.[key]);
  const dirty = Object.keys(draft).length > 0;

  return (
    <Card>
      <CardHeader
        title={t(group.title)}
        subtitle={group.note ? t(group.note) : undefined}
        action={
          <Button
            variant="primary"
            icon={<Save className="h-4 w-4" />}
            disabled={!dirty}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            {t('common.save')}
          </Button>
        }
      />

      <div className="grid gap-5 sm:grid-cols-2">
        {group.settings.map((spec) => {
          const value = current(spec.key);
          const changed = spec.key in draft;

          return (
            <Field
              key={spec.key}
              label={
                <span className="flex items-center gap-2">
                  {t(spec.label)}
                  {changed && <Badge tone="warning">unsaved</Badge>}
                </span>
              }
              hint={
                <>
                  {spec.hint && <span className="block">{t(spec.hint)}</span>}
                  <span className="font-mono text-mono-sm" dir="ltr">{spec.key}</span>
                </>
              }
              error={problemFor(spec.key)}
              // A link reads whole across the card.
              className={spec.type === 'link' ? 'sm:col-span-2' : undefined}
            >
              {spec.type === 'boolean' ? (
                <Checkbox
                  checked={Boolean(value)}
                  onChange={(e) => setDraft({ ...draft, [spec.key]: e.target.checked })}
                  label={value ? t('common.yes') : t('common.no')}
                />
              ) : spec.type === 'weekday' ? (
                <Select
                  value={String(value ?? '')}
                  onChange={(e) => setDraft({ ...draft, [spec.key]: Number(e.target.value) })}
                >
                  {DAY_NAMES[locale].map((day, index) => (
                    <option key={day} value={index}>{day}</option>
                  ))}
                  {/* A value saved before this was a dropdown may be out of range — show it
                      rather than silently displaying Sunday over the top of it. */}
                  {!DAY_NAMES[locale][Number(value)] && (
                    <option value={String(value ?? '')}>{String(value ?? '—')}</option>
                  )}
                </Select>
              ) : spec.type === 'select' ? (
                <Select
                  value={String(value ?? '')}
                  onChange={(e) => setDraft({ ...draft, [spec.key]: e.target.value })}
                >
                  {spec.options?.map((o) => (
                    <option key={o} value={o}>{o === 'en' ? 'English' : o === 'ar' ? 'العربية' : enumLabel(o, locale)}</option>
                  ))}
                </Select>
              ) : spec.type === 'department' ? (
                <Select
                  value={String(value ?? '')}
                  onChange={(e) => setDraft({ ...draft, [spec.key]: e.target.value })}
                >
                  <option value="">{t('settings.portalNoDepartment')}</option>
                  {(departments?.items ?? []).map((d) => (
                    <option key={d.id} value={d.id}>{localized(d)}</option>
                  ))}
                </Select>
              ) : spec.type === 'link' ? (
                <Input
                  type="url"
                  dir="ltr"
                  placeholder="https://"
                  value={String(value ?? '')}
                  onChange={(e) => setDraft({ ...draft, [spec.key]: e.target.value })}
                />
              ) : spec.type === 'time' ? (
                <Input
                  type="time"
                  dir="ltr"
                  className="max-w-[9rem] tabular"
                  value={String(value ?? '')}
                  onChange={(e) => setDraft({ ...draft, [spec.key]: e.target.value })}
                />
              ) : spec.type === 'days' ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    max={30}
                    step={0.25}
                    className="max-w-[9rem] tabular"
                    value={String(value ?? '')}
                    onChange={(e) => setDraft({ ...draft, [spec.key]: Number(e.target.value) })}
                  />
                  <span className="text-body-sm text-muted">{t('settings.daysOfSalary')}</span>
                </div>
              ) : (
                <Input
                  type={spec.type === 'number' ? 'number' : 'text'}
                  value={String(value ?? '')}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      [spec.key]: spec.type === 'number' ? Number(e.target.value) : e.target.value,
                    })
                  }
                />
              )}
            </Field>
          );
        })}
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Attendance › who may pause work                                             */
/* -------------------------------------------------------------------------- */

/** How many employees may be picked to pause — the API's limit. */
const MAX_PAUSERS = 200;

/**
 * Who may pause work (operator, 2026-10-24: "in attendance settings i will chose the
 * employees who has right to pause work"): the administrators always, and the
 * employees picked here. A pause has no limit and no penalty of its own, but paused
 * time is not worked time: they still owe the day's hours, and a day that ends short
 * of them costs the short-day penalty above, as anyone's does.
 */
function PausePanel() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const pickerId = useId();

  const current = useQuery({
    queryKey: ['attendance', 'pausers'],
    queryFn: () => api.get<{ people: UserRef[] }>('/attendance/pausers'),
  });
  // Whom it can pick: every active employee — the list a mail goes to.
  const people = useQuery({
    queryKey: ['mailbox', 'recipients'],
    queryFn: () => api.get<MailRecipientOption[]>('/mailbox/recipients'),
    staleTime: 60_000,
  });
  // The short-day penalty the rule below names; the card above edits it.
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Record<string, Record<string, unknown>>>('/settings'),
  });
  // Names for those picked who are not on that list: whoever is editing, or someone switched off since.
  const known = useMemo(() => new Map<string, UserRef>((current.data?.people ?? []).map((p) => [p.id, p])), [current.data]);

  const [draft, setDraft] = useState<string[] | null>(null);
  const saved = useMemo(() => (current.data?.people ?? []).map((p) => p.id), [current.data]);
  const picked = draft ?? saved;
  const dirty = draft !== null && (draft.length !== saved.length || draft.some((id) => !saved.includes(id)));

  const save = useMutation({
    mutationFn: () => api.put('/attendance/pausers', { userIds: picked }),
    onSuccess: () => {
      toast.success(t('settings.saved'));
      setDraft(null);
      // The list, and the shift controls of whoever was picked or taken off.
      void queryClient.invalidateQueries({ queryKey: ['attendance'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const shortDayDays = settings.data?.attendance?.['attendance.shortDayPenaltyDays'];

  return (
    <Card>
      <CardHeader
        title={t('settings.pauseWork')}
        subtitle={t('settings.pauseWorkNote')}
        action={
          <Button
            variant="primary"
            icon={<Save className="h-4 w-4" />}
            disabled={!dirty || !current.data}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            {t('common.save')}
          </Button>
        }
      />
      {current.isLoading ? (
        <LoadingBlock />
      ) : current.error ? (
        <ErrorState error={current.error} onRetry={() => void current.refetch()} />
      ) : (
        <div className="space-y-3">
          {/* Not a Field: that is a <label>, and a label around the chips' buttons would press them. */}
          <div>
            <label htmlFor={pickerId} className="mb-1.5 flex items-center gap-2 text-body-sm font-medium text-[var(--text-secondary)]">
              {t('settings.pausers')}
              {dirty && <Badge tone="warning">unsaved</Badge>}
            </label>
            <PeoplePicker
              inputId={pickerId}
              people={people.data}
              value={picked}
              onChange={setDraft}
              max={MAX_PAUSERS}
              known={known}
              fullLabel={t('settings.pausersMax', { n: MAX_PAUSERS })}
            />
            <span className="mt-1 block text-caption text-faint">{t('settings.pausersHint')}</span>
          </div>
          <p className="max-w-3xl text-body-sm text-muted">
            {shortDayDays != null && Number(shortDayDays) > 0
              ? t('settings.pauseWorkRule', { days: formatDays(shortDayDays as number, locale) })
              : t('settings.pauseWorkRuleNoPenalty')}
          </p>
        </div>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Attendance › break types, Leave › leave types                               */
/* -------------------------------------------------------------------------- */

function BreakTypesPanel() {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['attendance', 'break-types', 'all'],
    queryFn: () => api.get<any[]>('/attendance/break-types', { all: true }),
  });

  return (
    <Card padded={false}>
      <CardHeader
        title={t('settings.breakTypes')}
        subtitle={t('settings.breakTypesNote')}
        className="p-5 pb-0"
        action={
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/settings/break-types/new')}>
            {t('settings.newBreakType')}
          </Button>
        }
      />
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data?.length ? (
        <EmptyState hint="" />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('common.name')}</Th>
              <Th>{t('common.code')}</Th>
              <Th align="end">{t('settings.allowanceMinutes')}</Th>
              <Th align="end">{t('settings.overrunPenalty')}</Th>
              <Th align="end">{t('settings.maxPerDay')}</Th>
              <Th align="end">{t('settings.order')}</Th>
              <Th>{t('settings.breakTakenBy')}</Th>
              <Th>{t('settings.countsAsWork')}</Th>
              <Th>{t('settings.needsApproval')}</Th>
              <Th>{t('common.status')}</Th>
              <Th align="end">{t('settings.timesUsed')}</Th>
            </tr>
          </thead>
          <tbody>
            {data.map((type) => (
              <Tr key={type.id} onClick={() => navigate(`/settings/break-types/${type.id}/edit`)}>
                <Td>{localized(type)}</Td>
                <Td className="font-mono text-mono-sm text-muted">{type.code}</Td>
                <Td align="end" className="tabular">
                  {type.maxMinutes ? formatNumber(type.maxMinutes, locale) : <span className="text-faint">{t('settings.noLimit')}</span>}
                </Td>
                {/* A type without a limit never overruns, so its penalty never applies. */}
                <Td align="end" className="whitespace-nowrap tabular">
                  {type.maxMinutes && Number(type.penaltyDays) > 0 ? (
                    formatDays(type.penaltyDays, locale)
                  ) : (
                    <span className="text-faint">—</span>
                  )}
                </Td>
                {/* How many times a day each employee may take it; empty, no limit (2026-10-28). */}
                <Td align="end" className="whitespace-nowrap tabular">
                  {type.maxPerDay ? formatNumber(type.maxPerDay, locale) : <span className="text-faint">{t('settings.noLimit')}</span>}
                </Td>
                <Td align="end" className="tabular text-muted">{type.sortOrder}</Td>
                <Td className="whitespace-nowrap">
                  {type.employees?.length > 0 ? (
                    <>
                      <Badge tone="info">{t('settings.breakForEmployees')}</Badge>
                      <span className="mt-1 block max-w-[16rem] whitespace-normal text-caption text-muted">
                        <bdi>{type.employees.map((p: UserRef) => localized(p)).join(locale === 'ar' ? '، ' : ', ')}</bdi>
                      </span>
                    </>
                  ) : type.departments?.length > 0 ? (
                    <>
                      <Badge tone="info">{t('settings.breakForDepartments')}</Badge>
                      <span className="mt-1 block max-w-[16rem] whitespace-normal text-caption text-muted">
                        <bdi>{type.departments.map((d: DepartmentRef) => localized(d)).join(locale === 'ar' ? '، ' : ', ')}</bdi>
                      </span>
                    </>
                  ) : type.adminsOnly ? (
                    <Badge tone="info">{t('settings.breakForAdmins')}</Badge>
                  ) : type.leadersOnly ? (
                    <Badge tone="info">{t('settings.breakForLeaders')}</Badge>
                  ) : (
                    <span className="text-muted">{t('settings.breakForEveryone')}</span>
                  )}
                </Td>
                {/* Yes: its time stays in Worked (a meeting). No: it comes off. */}
                <Td>
                  {type.countsAsWork ? (
                    <Badge tone="success">{t('common.yes')}</Badge>
                  ) : (
                    <span className="text-muted">{t('common.no')}</span>
                  )}
                </Td>
                {/* Yes: pressing it asks for approval, and it starts once someone approves —
                    the administrators, and whoever is picked on it, named under the badge. */}
                <Td>
                  {type.requiresApproval ? (
                    <>
                      <Badge tone="warning">{t('common.yes')}</Badge>
                      {type.approvers?.length > 0 && (
                        <span className="mt-1 block max-w-[16rem] text-caption text-muted">
                          <bdi>
                            {[t('settings.administrators'), ...type.approvers.map((p: UserRef) => localized(p))].join(
                              locale === 'ar' ? '، ' : ', ',
                            )}
                          </bdi>
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-muted">{t('common.no')}</span>
                  )}
                </Td>
                <Td>
                  <Badge tone={type.isActive ? 'success' : 'neutral'}>
                    {type.isActive ? t('settings.offered') : t('settings.switchedOff')}
                  </Badge>
                </Td>
                <Td align="end" className="tabular text-muted">{formatNumber(type._count.breaks, locale)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function LeaveTypesPanel() {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['leaves', 'types', 'all'],
    queryFn: () => api.get<any[]>('/leaves/types', { all: true }),
  });

  return (
    <Card padded={false}>
      <CardHeader
        title={t('settings.leaveTypes')}
        subtitle={t('settings.leaveTypesNote')}
        className="p-5 pb-0"
        action={
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/settings/leave-types/new')}>
            {t('settings.newLeaveType')}
          </Button>
        }
      />
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data?.length ? (
        <EmptyState hint="" />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('common.name')}</Th>
              <Th>{t('common.code')}</Th>
              <Th>{t('settings.isPaid')}</Th>
              <Th align="end">{t('settings.annualAllowance')}</Th>
              <Th align="end">{t('settings.order')}</Th>
              <Th>{t('common.status')}</Th>
              <Th align="end">{t('settings.timesUsed')}</Th>
            </tr>
          </thead>
          <tbody>
            {data.map((type) => (
              <Tr key={type.id} onClick={() => navigate(`/settings/leave-types/${type.id}/edit`)}>
                <Td>{localized(type)}</Td>
                <Td className="font-mono text-mono-sm text-muted">{type.code}</Td>
                <Td className="text-muted">{type.isPaid ? t('leaves.paid') : t('leaves.unpaid')}</Td>
                <Td align="end" className="tabular">
                  {type.annualAllowanceDays != null ? (
                    formatNumber(type.annualAllowanceDays, locale)
                  ) : (
                    <span className="text-faint">{t('leaves.noAllowance')}</span>
                  )}
                </Td>
                <Td align="end" className="tabular text-muted">{type.sortOrder}</Td>
                <Td>
                  <Badge tone={type.isActive ? 'success' : 'neutral'}>
                    {type.isActive ? t('settings.offered') : t('settings.switchedOff')}
                  </Badge>
                </Td>
                <Td align="end" className="tabular text-muted">{formatNumber(type._count.requests, locale)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Email                                                                       */
/* -------------------------------------------------------------------------- */

/** Is ZeptoMail configured, what went out lately, and a test to prove it works. */
function EmailPanel() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['email', 'status'],
    queryFn: () => api.get<any>('/email/status'),
    refetchInterval: 15_000,
  });

  const test = useMutation({
    mutationFn: () => requestWithMessage<any>('/email/test', { method: 'POST' }),
    onSuccess: ({ data: sent, message }) => {
      if (sent.status === 'SENT') toast.success(message ?? '');
      else toast.error(message ?? '');
      void queryClient.invalidateQueries({ queryKey: ['email', 'status'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const counts = Object.entries(data.lastSevenDays ?? {}) as Array<[string, number]>;

  return (
    <>
      <Card>
        <CardHeader
          title={t('email.delivery')}
          subtitle={
            !data.enabled
              ? t('email.disabled')
              : data.configured
                ? t('email.configured')
                : t('email.notConfigured', { vars: data.missing.join(', ') })
          }
          action={
            <Button icon={<Mail className="h-4 w-4" />} loading={test.isPending} onClick={() => test.mutate()}>
              {t('email.sendTest')}
            </Button>
          }
        />
        <dl className="grid gap-x-8 sm:grid-cols-2">
          <Detail label={t('email.from')}>
            <span dir="ltr">{data.fromAddress ? `${data.fromName} <${data.fromAddress}>` : '—'}</span>
          </Detail>
          <Detail label={t('email.endpoint')}>
            <span className="font-mono text-mono-sm" dir="ltr">{data.endpoint}</span>
          </Detail>
          <Detail label={t('email.links')}>
            <span className="font-mono text-mono-sm" dir="ltr">{data.adminUrl}</span>
          </Detail>
          <Detail label={t('email.lastSevenDays')}>
            {counts.length ? (
              <span className="flex flex-wrap justify-end gap-1.5">
                {counts.map(([status, count]) => (
                  <Badge key={status} tone={emailTone(status)} className="tabular">
                    {enumLabel(status, locale)} · {count}
                  </Badge>
                ))}
              </span>
            ) : (
              '—'
            )}
          </Detail>
        </dl>
      </Card>

      <Card padded={false}>
        <CardHeader title={t('email.recent')} className="p-5 pb-0" />
        {!data.recent.length ? (
          <EmptyState title={t('email.none')} hint="" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('common.createdAt')}</Th>
                <Th>{t('email.to')}</Th>
                <Th>{t('email.subject')}</Th>
                <Th>{t('common.status')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((m: any) => (
                <Tr key={m.id}>
                  <Td className="whitespace-nowrap text-muted">
                    <span title={formatDateTime(m.createdAt, locale)}>{relativeTime(m.createdAt, locale)}</span>
                  </Td>
                  <Td>
                    {/* A name or subject may be in either language: each keeps its own direction. */}
                    <span className="block" dir="auto">{m.toName ?? m.toAddress}</span>
                    <span className="text-caption text-faint" dir="ltr">{m.toAddress}</span>
                  </Td>
                  <Td className="max-w-md">
                    <span className="block truncate" dir="auto" title={m.subject}>{m.subject}</span>
                    {m.eventType && <span className="font-mono text-mono-sm text-faint" dir="ltr">{m.eventType}</span>}
                  </Td>
                  <Td>
                    <Badge tone={emailTone(m.status)}>{enumLabel(m.status, locale)}</Badge>
                    {m.lastError && (
                      <span className="mt-0.5 block max-w-xs truncate text-caption text-error-fg dark:text-error-dark" title={m.lastError}>
                        {m.lastError}
                      </span>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

function emailTone(status: string) {
  if (status === 'SENT') return 'success' as const;
  if (status === 'FAILED') return 'error' as const;
  if (status === 'SKIPPED') return 'neutral' as const;
  return statusTone(status);
}

/* -------------------------------------------------------------------------- */
/* Background jobs                                                             */
/* -------------------------------------------------------------------------- */

/** §45 — the durable job queue's health, so a stuck SLA job is visible. */
function JobsPanel() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['jobs', 'status'],
    queryFn: () => api.get<any>('/jobs/status'),
    refetchInterval: 15_000,
  });

  const runNow = useMutation({
    mutationFn: () => api.post('/jobs/run-now'),
    onSuccess: () => {
      toast.success('Queue polled.');
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader
          title={t('settings.jobs')}
          subtitle={`Polling every ${data.pollSeconds}s · ${data.enabled ? 'enabled' : 'disabled'}`}
          action={
            <Button icon={<RefreshCw className="h-4 w-4" />} loading={runNow.isPending} onClick={() => runNow.mutate()}>
              {t('common.refresh')}
            </Button>
          }
        />
        <p className="mb-4 text-body-sm text-muted">
          SLA deadlines run as durable database-backed jobs rather than in-memory timers, so a restart
          never loses one, and every handler is idempotent — a redelivery creates nothing twice (§34, §35).
        </p>
        <div className="flex flex-wrap gap-2">
          {data.counts.map((c: any) => (
            <Badge key={`${c.status}-${c.type}`} tone={statusTone(c.status)}>
              {c.type} · {c.status} · {c.count}
            </Badge>
          ))}
        </div>
      </Card>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card padded={false}>
          <CardHeader title="Upcoming" className="p-5 pb-0" />
          {data.upcoming.length ? (
            <Table>
              <thead>
                <tr>
                  <Th>Type</Th>
                  <Th align="end">Runs at</Th>
                </tr>
              </thead>
              <tbody>
                {data.upcoming.map((job: any) => (
                  <Tr key={job.id}>
                    <Td className="font-mono text-mono-sm">{job.type}</Td>
                    <Td align="end" className="tabular text-muted">{formatDateTime(job.runAt, locale)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <EmptyState title={t('dash.nothingPending')} hint="" />
          )}
        </Card>

        <Card padded={false}>
          <CardHeader title="Failed" className="p-5 pb-0" />
          {data.failed.length ? (
            <Table>
              <thead>
                <tr>
                  <Th>Type</Th>
                  <Th>Error</Th>
                  <Th align="end">Attempts</Th>
                </tr>
              </thead>
              <tbody>
                {data.failed.map((job: any) => (
                  <Tr key={job.id}>
                    <Td className="font-mono text-mono-sm">{job.type}</Td>
                    <Td className="max-w-xs truncate text-error-fg dark:text-error-dark">{job.lastError}</Td>
                    <Td align="end" className="tabular">{job.attempts}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <EmptyState title="No failed jobs" hint="" />
          )}
        </Card>
      </div>
    </div>
  );
}
